import React, { useEffect, useState } from "react";
import { handlePrintTicket, handleShareTicket } from "./handlePrintTicket";
import { Button } from "react-bootstrap";
import { Printer } from "lucide-react";
import { apiCall } from "../../Apis/apiCall";
import { RECEIPT_IMG } from "../../utils/Contants";

// === MAPPING: Backend to Frontend ===
const mapLayoutFromBackend = (value) => {
  const map = {
    "image_top_both": "img-top-both",
    "name_top_both": "name-top-both",
    "logo_only": "img-top-logo",
    "name_only": "img-top-name",
    "side_by_side": "side-by-side-both",
  };
  return map[value] || "img-top-both";
};

// Helper to extract layout and content
const getLayout = (value) => {
  if (value.includes("img")) return "img";
  if (value.includes("name-top")) return "name";
  if (value.includes("side")) return "side";
  return "img";
};

const getContent = (value) => {
  if (value.includes("both")) return "both";
  if (value.includes("logo")) return "logo";
  if (value.includes("name")) return "name";
  return "both";
};

const ReceiptComponent = ({ data, fieldName }) => {
  const [loading, setLoading] = useState(false);
  const [receiptImagePreview, setReceiptImagePreview] = useState("");
  const [templateData, setTemplateData] = useState({
    receipt_logo_url: "",
    receipt_company_name: "",
    receipt_footer_text: "",
    receipt_layout: "img-top-both", // frontend format
  });
  const receiptData = data || {};
  const {
    receipt_number,
    receipt_date,
    ticket_number,
    vehicle,
    parking,
    location,
  } = receiptData;

const my_date = receipt_date;
const d = new Date(my_date);

// Date only (UTC)
const date = `${String(d.getUTCDate()).padStart(2,"0")}/${String(d.getUTCMonth()+1).padStart(2,"0")}/${d.getUTCFullYear()}`;

// Time only (UTC, 12-hour)
let hours = d.getUTCHours();
const minutes = String(d.getUTCMinutes()).padStart(2, "0");
const ampm = hours >= 12 ? "PM" : "AM";
hours = hours % 12;
hours = hours ? hours : 12;
const time = `${String(hours).padStart(2,"0")}:${minutes} ${ampm}`;



  let taxBreakdown = [];
  try {
    taxBreakdown = parking?.tax_breakdown
      ? typeof parking.tax_breakdown === "string"
        ? JSON.parse(parking.tax_breakdown)
        : parking.tax_breakdown
      : [];
    if (!Array.isArray(taxBreakdown)) {
      taxBreakdown = [];
    }
  } catch (error) {
    taxBreakdown = [];
  }

  useEffect(() => {
    fetchTemplateData();
  }, []);

  const fetchTemplateData = async () => {
    setLoading(true);
    try {
      const response = await apiCall("get", "/get-receipt-ticket-temp");
      if (response.data.success) {
        const data = response.data.data;
        console.log("Fetched Data:", data);

        // Ensure layout is properly mapped from backend
        setTemplateData({
          receipt_logo_url: data.receipt_logo_url || "",
          receipt_company_name: data.receipt_company_name || "",
          receipt_footer_text: data.receipt_footer_text || "",
          receipt_layout: mapLayoutFromBackend(data.receipt_layout), // Convert to frontend
        });

        // Build full URLs for form preview
        if (data.receipt_logo_url) {
          const receiptUrl = `${RECEIPT_IMG}/${data.receipt_logo_url}`;
          console.log("Receipt Logo URL:", receiptUrl);
          setReceiptImagePreview(receiptUrl);
        }
      }
    } catch (error) {
      console.error("Error fetching template data:", error);
      setReceiptImagePreview("");
    } finally {
      setLoading(false);
    }
  };


  // Header component based on layout - EXACT SAME LOGIC as TicketComponent
  const renderHeader = () => {
    const layoutValue = templateData.receipt_layout; // Frontend format: "img-top-both", etc.
    const layout = getLayout(layoutValue); // Extract: "img", "name", "side"
    const content = getContent(layoutValue); // Extract: "both", "logo", "name"

    console.log("Current Receipt Layout:", layoutValue, "| Type:", layout, "| Content:", content);

    const logoUrl = receiptImagePreview;
    const name = templateData.receipt_company_name || "PARK EASE VALET";

    const showLogo = logoUrl && content !== "name";
    const showName = name && content !== "logo";

    if (!showLogo && !showName) return null;

    // Layout: img (image on top)
    if (layout === "img") {
      return (
        <div className="text-center mb-3">
          {showLogo && (
            <img
              src={logoUrl}
              alt="Receipt Logo"
              style={{ height: "60px", objectFit: "contain" }}
              onError={(e) => {
                console.error("Preview Receipt image failed:", logoUrl);
                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjYwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPklNRzwvdGV4dD48L3N2Zz4=";
              }}
            />
          )}
          {showName && (
            <h6 className="mt-2 mb-0 text-neutral-900 font-weight-bold">
              {name}
            </h6>
          )}
        </div>
      );
    }

    // Layout: name (name on top)
    if (layout === "name") {
      return (
        <div className="text-center mb-3">
          {showName && (
            <h6 className="mb-2 text-neutral-900 font-weight-bold">
              {name}
            </h6>
          )}
          {showLogo && (
            <img
              src={logoUrl}
              alt="Receipt Logo"
              style={{ height: "60px", objectFit: "contain" }}
              onError={(e) => {
                console.error("Preview Receipt image failed:", logoUrl);
                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjYwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPklNRzwvdGV4dD48L3N2Zz4=";
              }}
            />
          )}
        </div>
      );
    }

    // Layout: side (side-by-side)
    if (layout === "side") {
      return (
        <div className="d-flex align-items-center gap-3 mb-3 justify-content-center">
          {showLogo && (
            <img
              src={logoUrl}
              alt="Receipt Logo"
              style={{ height: "50px", objectFit: "contain" }}
              onError={(e) => {
                console.error("Preview Receipt image failed:", logoUrl);
                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjYwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPklNRzwvdGV4dD48L3N2Zz4=";
              }}
            />
          )}
          {showName && (
            <h6 className="mb-0 text-neutral-900 font-weight-bold">
              {name}
            </h6>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="receipt-container d-flex justify-content-center flex-column align-items-center">
      <div
        className={`receipt-mockup-${fieldName} position-relative overflow-hidden`}
        style={{
          fontFamily: "var(--default-font)",
          fontSize: "var(--font-sm)",
          width: "400px",
          background: "var(--white)",
          boxShadow: "var(--shadow-4)",
          borderRadius: "var(--rounded-8)",
          border: "1px solid var(--neutral-300)",
          marginBottom: "var(--size-16)",
        }}
      >
        {/* Dynamic Header based on layout */}
        <div className="p-3 pb-2">
          {renderHeader()}
        </div>

        <div
          className="d-flex justify-content-between p-3 pb-2"
          style={{ borderBottom: `2px dashed var(--neutral-300)` }}
        >
          {[
            { label: "Receipt #:", value: receipt_number || "-" },
            {
              label: "Date:",
              value: receipt_date
                ? date
                : "-",
            },
            {
              label: "Time:",
              value: receipt_date
                ? time
                : "-",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="d-flex flex-column mb-1"
              style={{ color: "var(--text-secondary-light)" }}
            >
              <span>{item.label}</span>
              <span
                className="fw-medium"
                style={{ color: "var(--text-primary-light)" }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
        <div
          className="p-3 pb-2"
          style={{ borderBottom: `2px dashed var(--neutral-300)` }}
        >
          <div
            className="fw-bold mb-2"
            style={{
              color: "var(--neutral-700)",
              fontSize: "var(--font-md)",
            }}
          >
            Vehicle Details
          </div>
          {[
            { label: "Ticket #:", value: ticket_number || "-" },
            { label: "Plate Number:", value: vehicle?.plate || "-" },
            { label: "Vehicle Type:", value: vehicle?.type || "-" },
            { label: "Vehicle Make:", value: vehicle?.make || "-" },
            { label: "Vehicle Model:", value: vehicle?.model || "-" },
            { label: "Vehicle Color:", value: vehicle?.color || "-" },
          ].map((item, index) => (
            <div
              key={index}
              className="d-flex justify-content-between mb-1"
              style={{ color: "var(--text-secondary-light)" }}
            >
              <span>{item.label}</span>
              <span
                className="fw-medium"
                style={{ color: "var(--text-primary-light)" }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
        <div
          className="p-3 pb-2"
          style={{ borderBottom: `2px dashed var(--neutral-300)` }}
        >
          <div
            className="fw-bold mb-2"
            style={{
              color: "var(--neutral-700)",
              fontSize: "var(--font-md)",
            }}
          >
            Parking Details
          </div>
          {[
            {
              label: "Start Parking:",
              value: parking?.parked_at
                ? new Date(parking.parked_at).toLocaleString("en-US", {
                    hour12: true,
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-",
            },
            {
              label: "End Parking:",
              value: parking?.unparked_at
                ? new Date(parking.unparked_at).toLocaleString("en-US", {
                    hour12: true,
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-",
            },
            { label: "Duration:", value: parking?.duration || "-" },
            { label: "Branch:", value: location?.branch || "-" },
            { label: "Location:", value: location?.location || "-" },
            ...(location?.entry_gate
              ? [{ label: "Entry Gate:", value: location.entry_gate }]
              : []),
            ...(location?.slot
              ? [{ label: "Slot:", value: location.slot }]
              : []),
          ].map((item, index) => (
            <div
              key={index}
              className="d-flex justify-content-between mb-1"
              style={{ color: "var(--text-secondary-light)" }}
            >
              <span>{item.label}</span>
              <span
                className="fw-medium"
                style={{ color: "var(--text-primary-light)" }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
        <div className="p-3">
          <div
            className="fw-bold mb-2"
            style={{
              color: "var(--neutral-700)",
              fontSize: "var(--font-md)",
            }}
          >
            Payment Breakdown
          </div>
          <div
            className="d-flex justify-content-between mb-1"
            style={{ color: "var(--text-secondary-light)" }}
          >
            <span>Valet Amount:</span>
            <span
              className="fw-medium"
              style={{ color: "var(--text-primary-light)" }}
            >
              {parking?.payment_amount || "0.00"}
            </span>
          </div>
          <div
            className="d-flex justify-content-between mb-1"
            style={{ color: "var(--text-secondary-light)" }}
          >
            <span>Parking Fee:</span>
            <span
              className="fw-medium"
              style={{ color: "var(--text-primary-light)" }}
            >
              {parking?.parking_fee_collected || "0.00"}
            </span>
          </div>
          {taxBreakdown.length > 0 && (
            <>
              <div
                className="fw-bold mt-2 mb-1"
                style={{
                  color: "var(--neutral-700)",
                  fontSize: "var(--font-sm)",
                }}
              >
                Total Tax:
              </div>
              {taxBreakdown.map((tax, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-between mb-1"
                  style={{ color: "var(--text-secondary-light)" }}
                >
                  <span>
                    {tax.type || "Tax"} ({tax.percent || "0"}%):
                  </span>
                  <span
                    className="fw-medium"
                    style={{ color: "var(--text-primary-light)" }}
                  >
                    {tax.amount || "0.00"}
                  </span>
                </div>
              ))}
            </>
          )}
          <div
            className="d-flex justify-content-between pt-2"
            style={{ borderTop: `1px solid var(--neutral-300)` }}
          >
            <span
              className="fw-bold"
              style={{
                fontSize: "var(--font-md)",
                color: "var(--text-primary-light)",
              }}
            >
              TOTAL:
            </span>
            <span
              className="fw-bold"
              style={{
                fontSize: "var(--font-md)",
                color: "var(--text-primary-light)",
              }}
            >
              {parking?.total_amount || "0.00"}
            </span>
          </div>
        </div>

        {/* Footer with custom text from API */}
        <div className="d-flex flex-column align-items-center justify-content-center text-center py-3 border-top">
          {templateData.receipt_footer_text && (
            <div 
              className="mb-2 font-weight-medium"
              style={{ color: "var(--neutral-700)", fontSize: "var(--font-sm)" }}
            >
              {templateData.receipt_footer_text}
            </div>
          )}
          <span className="small" style={{ color: "var(--neutral-600)" }}>
            Powered by{" "}
            <span className="fw-semibold text-primary ms-1">PARK EASE</span>
          </span>
        </div>

        <div
          className="position-absolute"
          style={{
            top: "-var(--size-8)",
            right: "-var(--size-8)",
            width: "var(--size-16)",
            height: "var(--size-16)",
            background: "var(--primary-400)",
            borderRadius: "var(--rounded-48)",
            opacity: 0.3,
          }}
        />
        <div
          className="position-absolute"
          style={{
            bottom: "-var(--size-4)",
            left: "-var(--size-4)",
            width: "var(--size-12)",
            height: "var(--size-12)",
            background: "var(--accent)",
            borderRadius: "var(--rounded-48)",
            opacity: 0.3,
          }}
        />
      </div>
      <div className="d-flex gap-3 mt-3">
        <Button
          variant="outline-primary"
          onClick={() => handleShareTicket(receiptData, "receipt", receiptImagePreview, templateData)}
          className="d-flex align-items-center gap-2"
          style={{ minWidth: "120px" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z" />
          </svg>
          Share Receipt
        </Button>
        <Button
          variant="outline-info"
          onClick={() => handlePrintTicket(receiptData, "receipt", receiptImagePreview, templateData)}
          className="d-flex align-items-center gap-2"
        >
          <Printer size={16} /> Print
        </Button>
      </div>
    </div>
  );
};

export default ReceiptComponent;